<template>
  <v-app>
    <!-- Search Form -->
    <v-container fluid>
      <v-expansion-panels v-model="panel">
        <v-expansion-panel>
          <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
            Search Form
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <v-form @submit.prevent="submit">
              <v-row class="px-2 pt-4 pb-0">
                <v-col lg="3" md="3" cols="12">
                  <v-autocomplete label="RFQ" variant="outlined" density="compact" bg-color="grey-lighten-4"
                    v-model="rfq_master_id.value.value" :items="getQuotationableRfqList" :item-title="(item) => item.code"
                    item-value="id" :error-messages="rfq_master_id.errorMessage.value" autocomplete="off" clearable>
                    <template #label>
                      RFQ <span class="text-red">*</span>
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col lg="3" md="3" cols="12">
                  <v-autocomplete label="Evaluation Criteria" variant="outlined" density="compact"
                    bg-color="grey-lighten-4" v-model="pur_cs_evaluation_configuration_master_id.value.value"
                    :items="getEvaluationCriteriaList"
                    :item-title="(item) => `${item.name} (${item.effective_from_date} - ${item.effective_to_date})`"
                    item-value="id" :error-messages="pur_cs_evaluation_configuration_master_id.errorMessage.value"
                    autocomplete="off" clearable>
                    <template #label>
                      Evaluation Criteria <span class="text-red">*</span>
                    </template>
                  </v-autocomplete>
                </v-col>

                <v-col lg="2" md="2" cols="12">
                  <v-btn class="bg-brand w-15" type="submit" :loading="loading">
                    Search
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <!-- Data Table Section -->
    <v-container fluid style="margin-top: -20px;">
      <v-expansion-panels v-model="panel">
        <v-expansion-panel>
          <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
            CS Quotation Evaluation Parameter Generate
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <!-- Show message if no data -->
            <v-alert v-if="getSchema.length === 0" type="info" variant="tonal" class="mb-4">
              No data available. Please select RFQ and Evaluation Criteria and click Search.
            </v-alert>

            <v-table v-else density="compact" class="compact-table">
              <thead>
                <tr>
                  <th class="text-left font-weight-bold compact-cell">Criteria</th>
                  <th v-for="item in getSchema" :key="item.supplier_id" class="text-center font-weight-bold compact-cell">
                    {{ item.supplier?.supplier_name || "N/A" }}
                    <div class="text-caption text-grey">
                      Rank: {{ item.rank || "-" }}
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                <!-- Dynamic criteria rows -->
                <tr v-for="criteria in selectedCriteria" :key="criteria.id">
                  <td class="font-weight-medium compact-cell">
                    {{ formatFieldName(criteria.field_name) }}
                  </td>
                  <td v-for="item in getSchema" :key="item.supplier_id + '-' + criteria.field_name" class="text-center compact-cell">
                    {{ item.field_scores?.[criteria.field_name] || "-" }}
                  </td>
                </tr>

                <!-- Totals row -->
                <tr class="bg-grey-lighten-3">
                  <td class="font-weight-bold compact-cell">Total Score</td>
                  <td v-for="item in getSchema" :key="'total-' + item.supplier_id" class="text-center font-weight-bold compact-cell">
                    {{ item.total_score || 0 }}
                  </td>
                </tr>

                <!-- Select row with compact checkboxes -->
                <tr class="bg-blue-grey-lighten-5">
                  <td class="font-weight-bold compact-cell">Select</td>
                  <td v-for="supplier in getSchema" :key="'select-' + supplier.supplier_id" class="text-center compact-cell">
                    <v-checkbox 
                      v-model="supplierSelections[supplier.supplier_id]" 
                      color="primary" 
                      hide-details
                      density="compact"
                      class="d-inline-flex"
                      @change="openModal(supplier)"
                    />
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <!-- Selection Modal -->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="bg-primary text-white">
          <span class="text-h6">Supplier Selection</span>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-row>
            <v-col cols="12">
              <v-alert type="info" variant="tonal" density="compact">
                <strong>Selected Supplier:</strong> {{ selectedSupplier?.supplier?.supplier_name || 'N/A' }}
                <br>
                <strong>Total Score:</strong> {{ selectedSupplier?.total_score || 0 }}
                <br>
                <strong>Rank:</strong> {{ selectedSupplier?.rank || '-' }}
              </v-alert>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="selectionReason"
                label="Selection Reason"
                variant="outlined"
                density="compact"
                bg-color="grey-lighten-4"
                rows="4"
                :error-messages="reasonError"
                placeholder="Enter the reason for selecting this supplier..."
              >
                <template #label>
                  Selection Reason <span class="text-red">*</span>
                </template>
              </v-textarea>
            </v-col>

            <v-col cols="12">
              <v-date-input
                v-model="evaluationDate"
                label="Evaluation Date"
                density="compact"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                variant="outlined"
                bg-color="grey-lighten-4"
                autocomplete="off"
                clearable
                readonly
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="closeModal"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="savingSupplier"
            @click="saveSupplierSelection"
          >
            Save Selection
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import { useField, useForm } from "vee-validate";
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import { useStore } from "vuex";
import * as yup from "yup";

export default {
  name: "CsQuotationEvaluation",

  setup() {
    // Form validation
    const { handleSubmit } = useForm({
      validationSchema: yup.object({
        rfq_master_id: yup.number().required("RFQ is required"),
        pur_cs_evaluation_configuration_master_id: yup
          .number()
          .required("Evaluation Criteria is required"),
      }),
    });

    const pur_cs_evaluation_configuration_master_id = useField(
      "pur_cs_evaluation_configuration_master_id"
    );
    const rfq_master_id = useField("rfq_master_id");
    const loading = ref(false);
    const savingSupplier = ref(false);
    const store = useStore();
    const router = useRouter();

    // Modal state
    const dialog = ref(false);
    const selectedSupplier = ref(null);
    const selectionReason = ref("");
    const evaluationDate = ref(new Date().toISOString().split("T")[0]);
    const reasonError = ref("");

    // Supplier selections
    const supplierSelections = ref({});

    // Panel state
    const panel = ref([0]);

    // Computed properties
    const getQuotationableRfqList = computed(() => store.getters.getQuotationableRfqList || []);
    const getEvaluationCriteriaList = computed(
      () => store.getters.getEvaluationCriteriaList || []
    );
    const getSchema = computed(
      () => store.getters.getCsQuotationEvalGenerate || []
    );

    // Get selected criteria
    const selectedCriteria = computed(() => {
      const selectedConfig = getEvaluationCriteriaList.value.find(
        (item) => item.id === pur_cs_evaluation_configuration_master_id.value.value
      );
      return selectedConfig?.evaluation_criteria || [];
    });

    // Methods
    const formatFieldName = (name) => {
      if (!name) return "";
      return name
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const openModal = (supplier) => {
      if (supplierSelections.value[supplier.supplier_id]) {
        selectedSupplier.value = supplier;
        selectionReason.value = "";
        reasonError.value = "";
        evaluationDate.value = new Date().toISOString().split("T")[0];
        dialog.value = true;
      } else {
        // If unchecked, just close
        selectedSupplier.value = null;
      }
    };

    const closeModal = () => {
      dialog.value = false;
      // Uncheck the checkbox if modal is cancelled
      if (selectedSupplier.value) {
        supplierSelections.value[selectedSupplier.value.supplier_id] = false;
      }
      selectedSupplier.value = null;
      selectionReason.value = "";
      reasonError.value = "";
    };

    const saveSupplierSelection = () => {
      // Validation
      if (!selectionReason.value?.trim()) {
        reasonError.value = "Selection reason is required";
        return;
      }

      if (!selectedSupplier.value) {
        toast.error("No supplier selected");
        return;
      }

      const actualSupplierId = selectedSupplier.value.supplier?.id || selectedSupplier.value.supplier_id;

      if (!actualSupplierId) {
        toast.error("Invalid supplier selection");
        return;
      }

      savingSupplier.value = true;
      reasonError.value = "";

      const data = {
        cs_quotation_master_id: selectedSupplier.value.id,
        selected_supplier_id: actualSupplierId,
        rfq_master_id: rfq_master_id.value.value,
        pur_cs_evaluation_configuration_master_id:
        pur_cs_evaluation_configuration_master_id.value.value,
        selection_reason: selectionReason.value.trim(),
        evaluation_date: evaluationDate.value,
      };

      store
        .dispatch("saveUpdateSelectedSupplier", data)
        .then(() => {
          savingSupplier.value = false;
          dialog.value = false;
          toast.success("Supplier selection saved successfully");

          // Keep checkbox checked after successful save
          setTimeout(() => {
            router.push({ name: 'selected-supplier-list' });
          }, 2000);
        })
        .catch((err) => {
          savingSupplier.value = false;
          console.error("Save error:", err.response?.data);
          toast.error(
            err.response?.data?.message ||
            err.message ||
            "Failed to save supplier selection"
          );
        });
    };

    const submit = handleSubmit((values) => {
      loading.value = true;

      const params = {
        rfq_master_id: values.rfq_master_id,
        pur_cs_evaluation_configuration_master_id:
        values.pur_cs_evaluation_configuration_master_id,
      };

      store.dispatch("getCsQuotationEvalGenerate", params)
        .then(() => {
          loading.value = false;
          toast.success("Data loaded successfully");

          // Reset selections when new data is loaded
          supplierSelections.value = {};
        })
        .catch((err) => {
          loading.value = false;
          toast.error(
            err.response?.data?.message ||
            err.message ||
            "Failed to fetch data"
          );
        });
    });

    return {
      // Form fields
      submit,
      rfq_master_id,
      pur_cs_evaluation_configuration_master_id,
      loading,
      savingSupplier,
      panel,

      // Modal
      dialog,
      selectedSupplier,
      selectionReason,
      evaluationDate,
      reasonError,

      // Computed
      getQuotationableRfqList,
      getEvaluationCriteriaList,
      getSchema,
      selectedCriteria,

      // Supplier data
      supplierSelections,

      // Methods
      formatFieldName,
      openModal,
      closeModal,
      saveSupplierSelection,
    };
  },

  created() {
    // Load initial data
    // if (this.$store.getters.getQuotationableRfqList?.length === 0) {
      this.$store.dispatch("getQuotationableRfqList");
    // }
    if (this.$store.getters.getEvaluationCriteriaList?.length === 0) {
      this.$store.dispatch("getEvaluationCriteriaList");
    }
  },
};
</script>

<style scoped>
.compact-table {
  border: 1px solid #e0e0e0;
}

.compact-table thead tr th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.compact-cell {
  padding: 8px 12px !important;
  font-size: 0.875rem;
}

.font-weight-bold {
  font-weight: 700;
}

.font-weight-medium {
  font-weight: 500;
}

/* Reduce table row height */
.v-table tbody tr {
  height: 40px !important;
}

.v-table thead tr {
  height: 48px !important;
}
</style>